/**
 * The channel vocabulary, frontend side — labels and colours in one place.
 *
 * Six files kept their own maps and they did not agree. Meta was #3B82F6 in
 * ChannelSplitCard, AggregateMarketingView and MarketingPage, and #0668E1 on
 * CrossSellPage. Google was #22C55E in three and #4285F4 in one. TikTok was
 * both #000000 and #111827. So the same channel changed colour as you moved
 * between reports, which is exactly the cue a reader uses to tell series apart.
 *
 * The labels had drifted further. ChannelSplitCard knew `organic_search`,
 * ChannelPerformancePage knew `organic`, NewCustomersPage knew `sales_team` and
 * nothing else did, and every one of them fell back to rendering the raw id —
 * so Refunds and Churn showed `sales_team` and `other` in a column beside
 * reports showing "Sales Team" and "Other".
 *
 * This mirrors backend/src/services/attribution/channels.js. Both sides resolve
 * any spelling — 'Meta', 'meta', 'Direct', 'google_bigquery' — to one canonical
 * id before rendering, so a report fed Capitalized BigQuery labels and a report
 * fed canonical ids draw the same channel the same way.
 */

/**
 * Canonical channels, matching the backend registry.
 *
 * `aliases` are the spellings that resolve here: the BigQuery classifier's
 * Capitalized labels, the ad_spend ingestion names, and the older frontend ids.
 */
const CHANNELS = [
  { id: 'meta', label: 'Meta', traffic: 'Meta', color: '#3B82F6', aliases: ['facebook', 'instagram'] },
  { id: 'google', label: 'Google', traffic: 'Google', color: '#22C55E', aliases: ['google_bigquery'] },
  { id: 'bing', label: 'Bing', traffic: 'Bing', color: '#008373', aliases: ['microsoft', 'msads', 'microsoft_ads'] },
  { id: 'tiktok', label: 'TikTok', traffic: 'TikTok', color: '#111827', aliases: [] },
  { id: 'reddit', label: 'Reddit', traffic: 'Reddit', color: '#FF4500', aliases: [] },
  { id: 'sms', label: 'SMS', traffic: 'SMS', color: '#14B8A6', aliases: ['twilio'] },
  { id: 'email', label: 'Email', traffic: 'Email', color: '#EC4899', aliases: ['customer.io', 'customerio'] },
  { id: 'everflow', label: 'Everflow', traffic: 'Everflow', color: '#DAA520', aliases: ['affiliate'] },
  { id: 'katalys', label: 'Katalys', traffic: 'Katalys', color: '#8B5CF6', aliases: [] },
  { id: 'vibe', label: 'Vibe', traffic: null, color: '#A855F7', aliases: [] },
  { id: 'sales_team', label: 'Sales Team', traffic: null, color: '#7C3AED', aliases: ['call_center'] },
  { id: 'organic', label: 'Organic', traffic: 'Organic', color: '#F59E0B', aliases: ['organic_search'] },
  // 'Direct' is the BigQuery classifier's name for this bucket and 'other' is
  // the rule's. One bucket, two spellings — see the backend registry.
  { id: 'other', label: 'Other', traffic: 'Direct', color: '#9CA3AF', aliases: ['direct'] },
  // Not a channel: the customer nothing could place. Kept distinct from `other`
  // because "no evidence" and "evidence we do not bucket" are different gaps.
  { id: 'unattributed', label: 'Unattributed', traffic: null, color: '#9CA3AF', aliases: [] },
]

/**
 * Spend-only channels, which appear in ad_spend and never in attribution.
 *
 * MarketingPage renders whatever channel names the ingestion Lambda wrote, and
 * these have no classifier branch because no click id of theirs reaches a
 * landing page. They get a stable colour so the Marketing charts do not
 * reshuffle, but they are deliberately not in the registry above — putting them
 * there would offer them as attribution buckets that can never fill.
 */
const SPEND_ONLY_COLORS = {
  youtube: '#FF0000',
  snapchat: '#FFFC00',
  twitter: '#1DA1F2',
  linkedin: '#0A66C2',
  pinterest: '#E60023',
}

/**
 * Colours for a channel nothing recognises, cycled by position. None of them is
 * a registered channel's colour, so an unknown channel never reads as a known one.
 */
const DEFAULT_COLORS = [
  '#F97316', '#06B6D4', '#84CC16', '#EF4444', '#F43F5E',
  '#0EA5E9', '#FBBF24', '#6366F1', '#78716C', '#10B981',
]

const BY_ID = new Map(CHANNELS.map(c => [c.id, c]))

const ALIASES = new Map()
for (const channel of CHANNELS) {
  for (const spelling of [channel.id, channel.label, ...channel.aliases]) {
    ALIASES.set(String(spelling).toLowerCase(), channel.id)
  }
}

/** Resolve any spelling of a channel to its canonical id, or null. */
export function resolveChannel(value) {
  if (value === null || value === undefined) return null
  return ALIASES.get(String(value).trim().toLowerCase()) || null
}

/**
 * The display name for a channel.
 *
 * An unrecognised value is title-cased rather than dropped — MarketingPage
 * renders whatever ad_spend holds, and a channel added to the ingestion Lambda
 * should read as "Youtube" here rather than disappearing while someone adds it
 * to the registry.
 */
export function channelLabel(value) {
  const id = resolveChannel(value)
  if (id) return BY_ID.get(id).label
  if (!value) return 'Unknown'
  return String(value)
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
}

/**
 * The label the BigQuery classifier writes into traffic report rows.
 *
 * Not the same as the display label: the classifier's fallback is 'Direct'
 * where the display name is 'Other', and `vibe` and `sales_team` have no
 * BigQuery branch at all and so return null. Use this ONLY to key against rows
 * that came out of a traffic query; use `channelLabel` for anything a person
 * reads.
 */
export function trafficLabel(value) {
  const id = resolveChannel(value)
  return id ? BY_ID.get(id).traffic : null
}

/**
 * The colour for a channel, stable across every report.
 *
 * `index` only matters for a channel nothing recognises, which takes the next
 * default colour by position so two unknowns in one chart stay distinguishable.
 */
export function channelColor(value, index = 0) {
  const id = resolveChannel(value)
  if (id) return BY_ID.get(id).color
  const spendOnly = value && SPEND_ONLY_COLORS[String(value).toLowerCase()]
  if (spendOnly) return spendOnly
  return DEFAULT_COLORS[index % DEFAULT_COLORS.length]
}

/**
 * Tailwind classes for a channel badge, for the tables that render one.
 *
 * Kept beside the colours rather than derived from them: these are palette
 * tokens with light and dark variants, not the hex a chart series needs.
 */
const CHANNEL_BADGE_STYLES = {
  meta: 'bg-blue-500/15 text-blue-700 dark:text-blue-300',
  google: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
  bing: 'bg-teal-700/20 text-teal-800 dark:text-teal-200',
  sms: 'bg-teal-500/15 text-teal-700 dark:text-teal-300',
  reddit: 'bg-orange-500/15 text-orange-700 dark:text-orange-300',
  sales_team: 'bg-violet-500/15 text-violet-700 dark:text-violet-300',
  tiktok: 'bg-neutral-500/15 text-neutral-700 dark:text-neutral-300',
  email: 'bg-pink-500/15 text-pink-700 dark:text-pink-300',
  everflow: 'bg-yellow-500/15 text-yellow-700 dark:text-yellow-300',
  katalys: 'bg-purple-500/15 text-purple-700 dark:text-purple-300',
  vibe: 'bg-fuchsia-500/15 text-fuchsia-700 dark:text-fuchsia-300',
  organic: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
  // Spend-only: keyed by its ad_spend name, since it never resolves to an id.
  snapchat: 'bg-yellow-400/25 text-yellow-800 dark:text-yellow-300',
  other: 'bg-muted text-muted-foreground',
  unattributed: 'bg-muted text-muted-foreground',
}

export function channelBadgeStyle(value) {
  const id = resolveChannel(value) || (value ? String(value).trim().toLowerCase() : null)
  return (id && Object.hasOwn(CHANNEL_BADGE_STYLES, id)) ? CHANNEL_BADGE_STYLES[id] : CHANNEL_BADGE_STYLES.other
}

export { CHANNELS, DEFAULT_COLORS }
